import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  InputAdornment,
  CircularProgress,
  useTheme,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  IconButton,
  Stack,
  Switch,
  FormControlLabel,
  Autocomplete,
  Badge,
  TablePagination,
  Tabs,
  Tab,
} from "@mui/material";
import {
  SaveOutlined,
  TuneOutlined,
  CheckCircleOutline,
  CalculateOutlined,
  RefreshOutlined,
  EditOutlined,
  SearchOutlined,
  AddOutlined,
  InfoOutlined,
  WarningAmberOutlined,
  DownloadOutlined,
  CloseOutlined,
  ArrowForwardOutlined,
  PercentOutlined,
  AttachMoneyOutlined,
  BusinessOutlined,
  ScheduleOutlined,
  RestartAltOutlined,
  PolicyOutlined,
  ReceiptLongOutlined,
  AccountBalanceWalletOutlined,
  CreditCardOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { companyManagementUrl } from "../../config";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";
import { formatKES } from "../WalletManagement/WalletBalance";
import {
  walletMutate,
  CREATE_FEE_SCHEDULE_MUTATION,
  UPSERT_FEE_TIERS_MUTATION,
  WALLET_QUERY,
  createWalletClient,
} from "../../data/WalletService/walletGraphQL";
import DisbursementFeeSchedules from "./DisbursementFeeSchedules";
import WithdrawalFeeSchedules from "./WithdrawalFeeSchedules";
import RoyaltyConfigManagement from "./RoyaltyConfigManagement";
import OverdraftFacilityManagement from "./OverdraftFacilityManagement";

// Standard Platform Default Tier values
export const STANDARD_DEFAULT_TIER = {
  feeType: "PERCENTAGE",
  feeValue: 2.5,
  minFee: 20.0,
  maxFee: 500.0,
  royaltyType: "PERCENTAGE",
  royaltyValue: 1.0,
  isActive: true,
};

// Pricing tier presets for rapid 1-click configuration
export const PRICING_PRESETS = [
  {
    id: "standard",
    name: "Standard Platform Tier",
    description: "Default for standard MFI tenants (2.5% fee, min KES 20, max KES 500, 1.0% royalty)",
    config: {
      feeType: "PERCENTAGE",
      feeValue: 2.5,
      minFee: 20.0,
      maxFee: 500.0,
      royaltyType: "PERCENTAGE",
      royaltyValue: 1.0,
    },
  },
  {
    id: "enterprise",
    name: "High-Volume Enterprise",
    description: "Discounted fee for high disbursement volume (1.5% fee, min KES 15, max KES 350, 0.75% royalty)",
    config: {
      feeType: "PERCENTAGE",
      feeValue: 1.5,
      minFee: 15.0,
      maxFee: 350.0,
      royaltyType: "PERCENTAGE",
      royaltyValue: 0.75,
    },
  },
  {
    id: "microlender",
    name: "Micro-Lender / Low Volume",
    description: "Higher margin for smaller loan tickets (3.0% fee, min KES 25, max KES 600, 1.25% royalty)",
    config: {
      feeType: "PERCENTAGE",
      feeValue: 3.0,
      minFee: 25.0,
      maxFee: 600.0,
      royaltyType: "PERCENTAGE",
      royaltyValue: 1.25,
    },
  },
  {
    id: "flat",
    name: "Fixed Flat Rate",
    description: "Predictable flat fee per transaction (Flat KES 35 disbursement, KES 10 royalty)",
    config: {
      feeType: "FLAT",
      feeValue: 35.0,
      minFee: null,
      maxFee: null,
      royaltyType: "FLAT",
      royaltyValue: 10.0,
    },
  },
];

// Helper to calculate fees, floor/cap triggers, and net disbursement
export const calculateFeePreview = (principalAmount, config) => {
  const amount = Math.max(0, Number(principalAmount) || 0);
  if (!config) {
    return {
      rawFee: 0,
      finalFee: 0,
      floorTriggered: false,
      capTriggered: false,
      netDisbursed: amount,
      effectiveRate: 0,
      royalty: 0,
    };
  }

  let rawFee = 0;
  if (config.feeType === "PERCENTAGE") {
    rawFee = amount * ((Number(config.feeValue) || 0) / 100);
  } else {
    rawFee = Number(config.feeValue) || 0;
  }

  let finalFee = rawFee;
  let floorTriggered = false;
  let capTriggered = false;

  const minFee =
    config.minFee !== null && config.minFee !== undefined && config.minFee !== ""
      ? Number(config.minFee)
      : null;
  const maxFee =
    config.maxFee !== null && config.maxFee !== undefined && config.maxFee !== ""
      ? Number(config.maxFee)
      : null;

  if (minFee !== null && !isNaN(minFee) && finalFee < minFee) {
    finalFee = minFee;
    floorTriggered = true;
  }
  if (maxFee !== null && !isNaN(maxFee) && finalFee > maxFee) {
    finalFee = maxFee;
    capTriggered = true;
  }

  let royalty = 0;
  if (config.royaltyType === "PERCENTAGE") {
    royalty = amount * ((Number(config.royaltyValue) || 0) / 100);
  } else {
    royalty = Number(config.royaltyValue) || 0;
  }

  return {
    rawFee: Math.round(rawFee * 100) / 100,
    finalFee: Math.round(finalFee * 100) / 100,
    floorTriggered,
    capTriggered,
    netDisbursed: Math.max(0, amount - finalFee),
    effectiveRate: amount > 0 ? (finalFee / amount) * 100 : 0,
    royalty: Math.round(royalty * 100) / 100,
  };
};

const FeeConfigManagement = ({ companyId = null, onConfigUpdated }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  // Sub-Navigation Tab State
  const [configTab, setConfigTab] = useState(0);

  // Directory state
  const [tenants, setTenants] = useState([]);
  const [feeConfigsMap, setFeeConfigsMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterTier, setFilterTier] = useState("ALL");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Modal / Editor State
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form Fields
  const [targetCompanyId, setTargetCompanyId] = useState(companyId || "");
  const [feeType, setFeeType] = useState(STANDARD_DEFAULT_TIER.feeType);
  const [feeValue, setFeeValue] = useState(String(STANDARD_DEFAULT_TIER.feeValue));
  const [minFee, setMinFee] = useState(String(STANDARD_DEFAULT_TIER.minFee));
  const [maxFee, setMaxFee] = useState(String(STANDARD_DEFAULT_TIER.maxFee));
  const [royaltyType, setRoyaltyType] = useState(STANDARD_DEFAULT_TIER.royaltyType);
  const [royaltyValue, setRoyaltyValue] = useState(String(STANDARD_DEFAULT_TIER.royaltyValue));
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 10)
  );
  const [effectiveTo, setEffectiveTo] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedPreset, setSelectedPreset] = useState("standard");

  // Live Simulator State
  const [simulatorAmount, setSimulatorAmount] = useState(10000);
  const [simulatorCompanyId, setSimulatorCompanyId] = useState(companyId || "GLOBAL");

  // Fetch all companies & their fee configurations dynamically from database
  const fetchAllTenantsAndConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
      };

      const tenantMap = new Map();

      // Seed with currently authenticated user's institution from session token if available
      try {
        const rawToken = localStorage.getItem("decodedToken");
        if (rawToken) {
          const decoded = JSON.parse(rawToken);
          const sessionCid = parseInt(decoded?.CompanyId || decoded?.companyId || decoded?.CompanyID, 10);
          const sessionCname = decoded?.Company || decoded?.CompanyName || (sessionCid ? `Tenant #${String(sessionCid).padStart(4, "0")}` : null);
          if (sessionCid && sessionCname) {
            tenantMap.set(sessionCid, {
              id: sessionCid,
              name: sessionCname,
              isActive: true,
              availableBalance: 0,
              escrowedBalance: 0,
              hasPaybill: false,
            });
          }
        }
      } catch (e) {
        console.warn("[FeeConfig] Failed reading decodedToken:", e);
      }

      // 1. Fetch live companies from Company table — PRIMARY source
      try {
        const compRes = await fetchJsonDynamic(
          "companies",
          null,
          null,
          companyManagementUrl.uri
        );
        console.log("[FeeConfig] companies response:", compRes);

        if (compRes?.error) {
          console.error("[FeeConfig] companies GraphQL error:", compRes.error);
        }

        const rawResults = compRes?.results;
        if (rawResults) {
          try {
            const parsed = typeof rawResults === "string" ? JSON.parse(rawResults) : rawResults;
            console.log("[FeeConfig] parsed companies count:", Array.isArray(parsed) ? parsed.length : parsed);
            if (Array.isArray(parsed)) {
              // Filter active companies the way it is being done in MCB Switch
              const activeCompanies = parsed.filter((c) => c?.IsActive || c?.isActive);
              activeCompanies.forEach((c) => {
                // Support both PascalCase (C# HotChocolate) and camelCase
                const id = c.Id ?? c.id;
                const name = c.CompanyName ?? c.companyName ?? c.Name ?? c.name ?? `Company #${id}`;
                if (id) {
                  tenantMap.set(id, {
                    id,
                    name,
                    isActive: true,
                    availableBalance: 0,
                    escrowedBalance: 0,
                    hasPaybill: false,
                  });
                }
              });
            }
          } catch (pe) {
            console.error("[FeeConfig] Error parsing companies results:", pe, rawResults);
          }
        }
      } catch (err) {
        console.error("[FeeConfig] Could not fetch companies:", err.message, err);
      }

      console.log("[FeeConfig] tenantMap after companies fetch:", tenantMap.size, "entries");

      // Set tenants immediately so the dropdown is populated even before wallet enrichment (highest Id first like MCB Switch)
      const tenantListEarly = Array.from(tenantMap.values()).sort((a, b) => (b.id || 0) - (a.id || 0));
      if (tenantListEarly.length > 0) {
        setTenants(tenantListEarly);
      }

      // 2. Optionally enrich with wallet balances via GraphQL (non-blocking — failures are fine)
      try {
        const walletClient = createWalletClient();
        const tenantIds = Array.from(tenantMap.keys());
        const walletResults = await Promise.allSettled(
          tenantIds.map((cid) =>
            walletClient.query({
              query: WALLET_QUERY,
              variables: { companyId: cid },
            })
          )
        );
        walletResults.forEach((result, idx) => {
          if (result.status === "fulfilled") {
            const w = result.value?.data?.wallet;
            const cid = tenantIds[idx];
            if (w && cid) {
              const existing = tenantMap.get(cid);
              if (existing) {
                tenantMap.set(cid, {
                  ...existing,
                  availableBalance: w.availableBalance ?? 0,
                  escrowedBalance: w.escrowedBalance ?? 0,
                  hasPaybill: true,
                });
              }
            }
          }
        });
      } catch (err) {
        console.warn("[FeeConfig] wallet GraphQL enrichment failed (non-critical):", err.message);
      }

      // Final sorted tenant list (highest ID first like MCB Switch)
      const tenantList = Array.from(tenantMap.values()).sort((a, b) => (b.id || 0) - (a.id || 0));
      console.log("[FeeConfig] Final tenant list:", tenantList.length, "tenants");
      setTenants(tenantList);

      // 3. Fetch fee configs for each tenant from local persistence
      const configs = {};
      if (tenantList.length > 0) {
        tenantList.forEach((t) => {
          try {
            const cached = localStorage.getItem(`sababisha_fee_config_${t.id}`);
            if (cached) {
              configs[t.id] = JSON.parse(cached);
              return;
            }
          } catch (ce) {}
          configs[t.id] = { companyId: t.id, isDefault: true, isUnset: true };
        });
      }

      setFeeConfigsMap(configs);
    } catch (err) {
      console.error("[FeeConfig] Failed to load fee configuration directory:", err);
    } finally {
      setLoading(false);
    }
  }, []);


  useEffect(() => {
    fetchAllTenantsAndConfigs();
  }, [fetchAllTenantsAndConfigs]);

  // Open modal to edit a specific company
  const handleOpenEdit = useCallback((tenant) => {
    const selectedTenant = tenant || (tenants.length > 0 ? tenants[0] : null);
    const cid = selectedTenant ? selectedTenant.id : "";
    const existing = (cid && feeConfigsMap[cid] && !feeConfigsMap[cid].isUnset)
      ? feeConfigsMap[cid]
      : null; // null = unconfigured, show blank form

    setEditingCompany(selectedTenant);
    setTargetCompanyId(cid ? String(cid) : "");
    setFeeType(existing?.feeType || "PERCENTAGE");
    setFeeValue(existing?.feeValue !== undefined ? String(existing.feeValue) : "");
    setMinFee(existing?.minFee !== null && existing?.minFee !== undefined ? String(existing.minFee) : "");
    setMaxFee(existing?.maxFee !== null && existing?.maxFee !== undefined ? String(existing.maxFee) : "");
    setRoyaltyType(existing?.royaltyType || "PERCENTAGE");
    setRoyaltyValue(existing?.royaltyValue !== undefined ? String(existing.royaltyValue) : "");
    setEffectiveFrom(
      existing?.effectiveFrom
        ? new Date(existing.effectiveFrom).toISOString().slice(0, 10)
        : new Date().toISOString().slice(0, 10)
    );
    setEffectiveTo(
      existing?.effectiveTo ? new Date(existing.effectiveTo).toISOString().slice(0, 10) : ""
    );
    setIsActive(existing?.isActive !== undefined ? existing.isActive : true);
    setSelectedPreset("custom");
    setDialogOpen(true);
  }, [feeConfigsMap, tenants]);


  // Apply a preset tier
  const handleApplyPreset = (preset) => {
    setSelectedPreset(preset.id);
    setFeeType(preset.config.feeType);
    setFeeValue(String(preset.config.feeValue));
    setMinFee(preset.config.minFee !== null ? String(preset.config.minFee) : "");
    setMaxFee(preset.config.maxFee !== null ? String(preset.config.maxFee) : "");
    setRoyaltyType(preset.config.royaltyType);
    setRoyaltyValue(String(preset.config.royaltyValue));
  };

  // Validation
  const validationError = useMemo(() => {
    if (!targetCompanyId) return "Please specify a Target Company.";
    const fVal = parseFloat(feeValue);
    if (isNaN(fVal) || fVal < 0) return "Disbursement fee value must be a valid positive number.";
    if (feeType === "PERCENTAGE" && fVal > 100) return "Percentage fee cannot exceed 100%.";

    const rVal = parseFloat(royaltyValue);
    if (isNaN(rVal) || rVal < 0) return "Withdrawal royalty must be a valid positive number.";
    if (royaltyType === "PERCENTAGE" && rVal > 100) return "Percentage royalty cannot exceed 100%.";

    const parsedMin = minFee !== "" ? parseFloat(minFee) : null;
    const parsedMax = maxFee !== "" ? parseFloat(maxFee) : null;

    if (parsedMin !== null && parsedMax !== null && !isNaN(parsedMin) && !isNaN(parsedMax)) {
      if (parsedMin > parsedMax) {
        return "Minimum Fee Floor cannot exceed Maximum Fee Cap.";
      }
    }
    return null;
  }, [targetCompanyId, feeValue, feeType, royaltyValue, royaltyType, minFee, maxFee]);

  // Save handler
  const handleSaveConfig = async (e) => {
    if (e) e.preventDefault();
    if (validationError) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: validationError,
      });
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const headers = {
        Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
        "Content-Type": "application/json",
      };

      const payload = {
        companyId: parseInt(targetCompanyId, 10),
        feeType,
        feeValue: parseFloat(feeValue),
        royaltyType,
        royaltyValue: parseFloat(royaltyValue),
        minFee: minFee !== "" && !isNaN(parseFloat(minFee)) ? parseFloat(minFee) : null,
        maxFee: maxFee !== "" && !isNaN(parseFloat(maxFee)) ? parseFloat(maxFee) : null,
        effectiveFrom: effectiveFrom || null,
        effectiveTo: effectiveTo || null,
        isActive,
      };

      // ── 1. Create and Upsert Fee Schedule via HotChocolate GraphQL (Solution 2) ──
      try {
        const scheduleName = `Pricing Schedule - Company #${targetCompanyId} (${feeType} ${feeValue}${feeType === "PERCENTAGE" ? "%" : " KES"})`;
        const scheduleRes = await walletMutate(CREATE_FEE_SCHEDULE_MUTATION, {
          input: {
            name: scheduleName,
            effectiveFrom: effectiveFrom ? new Date(effectiveFrom).toISOString() : new Date().toISOString(),
            notes: `Tenant Institution #${targetCompanyId} fee policy. Fee: ${feeValue} (${feeType}), Min: ${minFee || "N/A"}, Max: ${maxFee || "N/A"}, Royalty: ${royaltyValue} (${royaltyType})`,
          },
        });

        const newScheduleId = scheduleRes?.createFeeSchedule;
        if (newScheduleId) {
          // Standard contiguous disbursement bands (0 to 1,000,000) matching architecture §7.6
          const standardBands = [
            { from: 0, to: 500, mpesa: 10 },
            { from: 501, to: 1000, mpesa: 15 },
            { from: 1001, to: 5000, mpesa: 22 },
            { from: 5001, to: 10000, mpesa: 28 },
            { from: 10001, to: 20000, mpesa: 35 },
            { from: 20001, to: 50000, mpesa: 45 },
            { from: 50001, to: 150000, mpesa: 60 },
            { from: 150001, to: 250000, mpesa: 75 },
            { from: 250001, to: 1000000, mpesa: 100 },
          ];

          const tiers = standardBands.map((band) => {
            const mid = (band.from + band.to) / 2;
            let charge = feeType === "PERCENTAGE" ? (mid * parseFloat(feeValue)) / 100 : parseFloat(feeValue);
            if (minFee !== "" && !isNaN(parseFloat(minFee))) charge = Math.max(charge, parseFloat(minFee));
            if (maxFee !== "" && !isNaN(parseFloat(maxFee))) charge = Math.min(charge, parseFloat(maxFee));
            const sababisha = Math.max(0, Math.round(charge - band.mpesa));
            return {
              amountFrom: band.from,
              amountTo: band.to,
              sababishaCharge: sababisha,
              mpesaCharge: band.mpesa,
            };
          });

          await walletMutate(UPSERT_FEE_TIERS_MUTATION, {
            input: {
              scheduleId: newScheduleId,
              tiers,
            },
          });
        }
      } catch (gqlErr) {
        console.warn("[FeeConfig] GraphQL fee schedule mutation:", gqlErr);
      }



      // ── 3. Persist tenant policy in localStorage cache & state map ──
      try {
        localStorage.setItem(`sababisha_fee_config_${payload.companyId}`, JSON.stringify({ ...payload, isDefault: false, isUnset: false }));
      } catch (ce) {}

      // Update local state map with real saved policy
      setFeeConfigsMap((prev) => ({
        ...prev,
        [payload.companyId]: { ...payload, isDefault: false, isUnset: false },
      }));

      Swal.fire({
        icon: "success",
        title: "Pricing Policy Saved",
        text: `Fee configuration for Company #${targetCompanyId} has been successfully applied to the database.`,
        timer: 2000,
        showConfirmButton: false,
      });

      setDialogOpen(false);
      if (onConfigUpdated) onConfigUpdated(payload);
    } catch (err) {
      console.error("Save fee config error:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.response?.data ||
        err.message ||
        "Failed to save fee configuration to database.";

      Swal.fire({
        icon: "error",
        title: "Save Failed",
        text: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
      });
    } finally {
      setSaving(false);
    }
  };

  // Reset company to standard platform tier
  const handleResetToStandard = async (company) => {
    const result = await Swal.fire({
      title: "Reset to Standard Tier?",
      text: `Revert pricing for ${company.name} (#${company.id}) to default 2.5% platform rates on the database?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#1766A2",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Reset Pricing",
    });

    if (result.isConfirmed) {
      try {


        try {
          localStorage.removeItem(`sababisha_fee_config_${company.id}`);
        } catch (ce) {}

        setFeeConfigsMap((prev) => ({
          ...prev,
          [company.id]: { companyId: company.id, ...STANDARD_DEFAULT_TIER, isDefault: true, isUnset: false },
        }));

        Swal.fire("Reset Complete", "Tenant pricing restored to Standard Platform Tier on database.", "success");
      } catch (err) {
        console.error("Reset fee config error:", err);
        const errMsg =
          err.response?.data?.message ||
          err.response?.data?.title ||
          err.response?.data ||
          err.message ||
          "Failed to reset fee policy on database.";
        Swal.fire("Reset Failed", typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg), "error");
      }
    }
  };

  // Filtered tenants list
  const filteredTenants = useMemo(() => {
    return tenants.filter((t) => {
      const matchesSearch =
        t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        String(t.id).includes(searchQuery);

      const config = feeConfigsMap[t.id];
      const isCustom = config && !config.isDefault;

      if (filterTier === "CUSTOM" && !isCustom) return false;
      if (filterTier === "STANDARD" && (isCustom || config?.isUnset)) return false;
      if (filterTier === "UNSET" && !config?.isUnset) return false;
      if (filterTier === "FLAT" && config?.feeType !== "FLAT") return false;
      if (filterTier === "PERCENTAGE" && config?.feeType !== "PERCENTAGE") return false;

      return matchesSearch;
    });
  }, [tenants, searchQuery, filterTier, feeConfigsMap]);

  // Reset pagination to first page when filtering or searching
  useEffect(() => {
    setPage(0);
  }, [searchQuery, filterTier]);

  // Paginated tenants slice for the directory table
  const paginatedTenants = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredTenants.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredTenants, page, rowsPerPage]);

  // Current active simulator config
  const activeSimulatorConfig = useMemo(() => {
    if (simulatorCompanyId === "GLOBAL") {
      const firstConfigured = Object.values(feeConfigsMap).find((c) => c && !c.isUnset);
      return firstConfigured || null;
    }
    const cfg = feeConfigsMap[simulatorCompanyId];
    return cfg && !cfg.isUnset ? cfg : null;
  }, [simulatorCompanyId, feeConfigsMap]);

  const simulationResult = useMemo(() => {
    if (!activeSimulatorConfig) return null;
    return calculateFeePreview(simulatorAmount, activeSimulatorConfig);
  }, [simulatorAmount, activeSimulatorConfig]);

  // In-modal live preview
  const modalPreviewResult = useMemo(() => {
    return calculateFeePreview(10000, {
      feeType,
      feeValue,
      minFee,
      maxFee,
      royaltyType,
      royaltyValue,
    });
  }, [feeType, feeValue, minFee, maxFee, royaltyType, royaltyValue]);

  // Export CSV of fee schedule
  const handleExportCSV = () => {
    const headers = [
      "Company ID",
      "Company Name",
      "Fee Type",
      "Fee Value",
      "Min Fee (Floor)",
      "Max Fee (Cap)",
      "Royalty Type",
      "Royalty Value",
      "Policy Status",
    ];

    const rows = tenants.map((t) => {
      const cfg = feeConfigsMap[t.id] || STANDARD_DEFAULT_TIER;
      return [
        t.id,
        `"${t.name.replace(/"/g, '""')}"`,
        cfg.feeType,
        cfg.feeValue,
        cfg.minFee || "None",
        cfg.maxFee || "None",
        cfg.royaltyType,
        cfg.royaltyValue,
        cfg.isActive !== false ? "Active" : "Inactive",
      ];
    });

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `Sababisha_Fee_Configuration_Schedule_${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Metrics
  const configuredCount = Object.values(feeConfigsMap).filter((c) => c && !c.isUnset).length;
  const unconfiguredCount = tenants.length - configuredCount;
  const flatFeeCount = Object.values(feeConfigsMap).filter((c) => c && !c.isUnset && c.feeType === "FLAT").length;
  const percentFeeCount = Object.values(feeConfigsMap).filter((c) => c && !c.isUnset && c.feeType === "PERCENTAGE").length;

  return (
    <Box sx={{ width: "100%" }}>
      {/* ── Fee Configuration Category Tabs ──────────────────────────────── */}
      <Tabs
        value={configTab}
        onChange={(_, val) => setConfigTab(val)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          borderBottom: 1,
          borderColor: "divider",
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: 700,
            fontSize: "0.9rem",
            minHeight: 48,
          },
          "& .MuiTabScrollButton-root": {
            width: 36,
            opacity: 1,
            color: "inherit",
            "&.Mui-disabled": { opacity: 0.3 },
          },
        }}
      >
        <Tab
          icon={<ReceiptLongOutlined />}
          iconPosition="start"
          label="Loan Disbursement Fee Tariff"
        />
        <Tab
          icon={<AccountBalanceWalletOutlined />}
          iconPosition="start"
          label="Treasury Withdrawal Fee Tariff"
        />
        <Tab
          icon={<PercentOutlined />}
          iconPosition="start"
          label="Royalty Configuration (% of Revenue)"
        />
        <Tab
          icon={<CreditCardOutlined />}
          iconPosition="start"
          label="Overdraft Facilities & Credit Lines"
        />
        <Tab
          icon={<TuneOutlined />}
          iconPosition="start"
          label="Tenant Pricing Overrides & Directory"
        />
      </Tabs>

      {/* ── Sub-Tab 0: Loan Disbursement Fee Schedules (Standard Tariff) ── */}
      {configTab === 0 && <DisbursementFeeSchedules />}

      {/* ── Sub-Tab 1: Treasury Withdrawal Fee Schedules (Independent Tariff) ── */}
      {configTab === 1 && <WithdrawalFeeSchedules />}

      {/* ── Sub-Tab 2: Company Royalty Configuration (% of Revenue per Period) ── */}
      {configTab === 2 && <RoyaltyConfigManagement tenants={tenants} />}

      {/* ── Sub-Tab 3: Investor Overdraft Facility Management & Governance ── */}
      {configTab === 3 && <OverdraftFacilityManagement tenants={tenants} />}

      {/* ── Sub-Tab 4: Tenant Pricing Directory, Overrides & Live Sandbox Simulator ── */}
      {configTab === 4 && (
        <>
          {/* ── Top Summary KPI Cards ─────────────────────────────────────── */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  CONFIGURED PRICING
                </Typography>
                <PercentOutlined sx={{ color: "#1766A2", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#1766A2" }}>
                {configuredCount} Active
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5 }}>
                {unconfiguredCount} tenants unconfigured
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  UNCONFIGURED TENANTS
                </Typography>
                <AttachMoneyOutlined sx={{ color: "#ed6c02", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: "#ed6c02" }}>
                {unconfiguredCount} Pending
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5 }}>
                Awaiting pricing setup
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  MFI TENANTS REGISTERED
                </Typography>
                <BusinessOutlined sx={{ color: "#22bf72", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                {tenants.length} Institutions
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5 }}>
                Total Companies
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              borderRadius: 2.5,
              backgroundColor: isDark ? colors.primary[400] : "#ffffff",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400] }}>
                  PRICING MODEL BREAKDOWN
                </Typography>
                <PolicyOutlined sx={{ color: "#9c27b0", fontSize: 20 }} />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                {flatFeeCount} Flat / {percentFeeCount} % Rate
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400], display: "block", mt: 0.5 }}>
                {unconfiguredCount} unconfigured
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ── Interactive Live Fee Simulator & Sandbox ─────────────────────── */}
      <Card
        sx={{
          mb: 3,
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CalculateOutlined sx={{ color: "#1766A2", fontSize: 24 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                  Interactive Fee Sandbox & Real-Time Impact Simulator
                </Typography>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Test loan disbursement scenarios against tenant floor/cap boundaries before deploying pricing changes.
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                select
                size="small"
                label="Simulate Tenant"
                value={
                  simulatorCompanyId === "GLOBAL" || tenants.some((t) => String(t.id) === String(simulatorCompanyId))
                    ? simulatorCompanyId
                    : "GLOBAL"
                }
                onChange={(e) => setSimulatorCompanyId(e.target.value)}
                sx={{ minWidth: 220 }}
              >
                <MenuItem value="GLOBAL">Standard Platform Default</MenuItem>
                {tenants.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.name} (#{t.id})
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Divider sx={{ mb: 2.5 }} />

          <Grid container spacing={3} alignItems="center">
            {/* Input Column */}
            <Grid item xs={12} md={5}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                Test Disbursement Principal Amount (KES)
              </Typography>
              <TextField
                fullWidth
                type="number"
                size="small"
                value={simulatorAmount}
                onChange={(e) => setSimulatorAmount(Number(e.target.value))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">KES</InputAdornment>,
                }}
              />

              {/* Quick Amount Chips */}
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", gap: 0.5 }}>
                {[1000, 5000, 20000, 50000, 100000].map((amt) => (
                  <Chip
                    key={amt}
                    label={`KES ${amt.toLocaleString()}`}
                    size="small"
                    clickable
                    color={simulatorAmount === amt ? "primary" : "default"}
                    onClick={() => setSimulatorAmount(amt)}
                    sx={{ fontWeight: 600 }}
                  />
                ))}
              </Stack>
            </Grid>

            {/* Simulation Results Column */}
            <Grid item xs={12} md={7}>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
                  border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
                }}
              >
                {simulationResult ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        CALCULATED FEE
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#1766A2", mt: 0.5 }}>
                        {formatKES(simulationResult.finalFee)}
                      </Typography>
                      {simulationResult.floorTriggered && (
                        <Chip
                          label="Floor Applied"
                          color="warning"
                          size="small"
                          sx={{ height: 20, fontSize: "0.65rem", mt: 0.5, fontWeight: 700 }}
                        />
                      )}
                      {simulationResult.capTriggered && (
                        <Chip
                          label="Cap Applied"
                          color="secondary"
                          size="small"
                          sx={{ height: 20, fontSize: "0.65rem", mt: 0.5, fontWeight: 700 }}
                        />
                      )}
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        NET DISBURSED
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#22bf72", mt: 0.5 }}>
                        {formatKES(simulationResult.netDisbursed)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        Borrower receives
                      </Typography>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        EFFECTIVE RATE
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100], mt: 0.5 }}>
                        {simulationResult.effectiveRate.toFixed(2)}%
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        Of principal
                      </Typography>
                    </Grid>

                    <Grid item xs={6} sm={3}>
                      <Typography variant="caption" sx={{ color: colors.grey[400], fontWeight: 600 }}>
                        EST. ROYALTY
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: "#ed6c02", mt: 0.5 }}>
                        {formatKES(simulationResult.royalty)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                        On bank withdrawal
                      </Typography>
                    </Grid>
                  </Grid>
                ) : (
                  <Box sx={{ py: 3, textAlign: "center" }}>
                    <Typography variant="body2" sx={{ color: colors.grey[400], fontWeight: 500 }}>
                      No pricing policy configured for this tenant. Click <strong>Configure Tenant Pricing</strong> below to set up disbursement fees and withdrawal royalties.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ── Tenant Pricing Policy Directory Table ────────────────────────── */}
      <Card
        sx={{
          borderRadius: 2.5,
          backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
        }}
      >
        <CardContent sx={{ p: 3 }}>
          {/* Action & Filter Toolbar */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
              mb: 3,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TuneOutlined sx={{ color: "#1766A2" }} />
              <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                Tenant Fee & Royalty Pricing Schedules
              </Typography>
            </Box>

            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search tenant name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined sx={{ color: colors.grey[400], fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 240 } }}
              />

              <TextField
                select
                size="small"
                value={filterTier}
                onChange={(e) => setFilterTier(e.target.value)}
                sx={{ width: 160 }}
              >
                <MenuItem value="ALL">All Pricing Tiers</MenuItem>
                <MenuItem value="CUSTOM">Custom Policies</MenuItem>
                <MenuItem value="STANDARD">Standard Platform</MenuItem>
                <MenuItem value="UNSET">Unconfigured / Default</MenuItem>
                <MenuItem value="PERCENTAGE">% Percentage Only</MenuItem>
                <MenuItem value="FLAT">Flat Fee Only</MenuItem>
              </TextField>

              <Tooltip title="Export current fee configuration schedule to CSV">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportCSV}
                  startIcon={<DownloadOutlined />}
                  sx={{ textTransform: "none", fontWeight: 600, borderColor: colors.grey[600] }}
                >
                  Export CSV
                </Button>
              </Tooltip>

              <Tooltip title="Refresh fee configurations from database">
                <IconButton
                  size="small"
                  onClick={fetchAllTenantsAndConfigs}
                  disabled={loading}
                  sx={{ border: `1px solid ${colors.grey[700]}`, borderRadius: 1.5 }}
                >
                  {loading ? <CircularProgress size={18} /> : <RefreshOutlined sx={{ fontSize: 18 }} />}
                </IconButton>
              </Tooltip>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddOutlined />}
                onClick={() => handleOpenEdit(null)}
                sx={{
                  backgroundColor: "#1766A2",
                  color: "#fff",
                  fontWeight: 700,
                  textTransform: "none",
                  px: 2,
                }}
              >
                Configure Tenant Pricing
              </Button>
            </Stack>
          </Box>

          {/* Directory Table */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : filteredTenants.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                No tenant pricing policies match your search filters.
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Tenant / Institution</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Disbursement Charge</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Floor & Cap Limits</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Withdrawal Royalty</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Pricing Tier</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTenants.map((t) => {
                    const cfg = feeConfigsMap[t.id];
                    const isConfigured = Boolean(cfg && !cfg.isUnset && (cfg.feeValue !== undefined || cfg.feeType));
                    const isCustom = isConfigured && !cfg.isDefault;

                    return (
                      <TableRow key={t.id} hover>
                        {/* Company Info */}
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {t.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            Tenant #{String(t.id).padStart(4, "0")}
                          </Typography>
                        </TableCell>

                        {/* Fee Rate */}
                        <TableCell>
                          {isConfigured ? (
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Chip
                                label={cfg.feeType === "PERCENTAGE" ? "% Rate" : "Flat Fee"}
                                size="small"
                                variant="outlined"
                                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 700 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 700, color: "#1766A2" }}>
                                {cfg.feeType === "PERCENTAGE"
                                  ? `${cfg.feeValue}%`
                                  : formatKES(cfg.feeValue)}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Floor & Cap */}
                        <TableCell>
                          {isConfigured ? (
                            <>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                                Min:{" "}
                                <span style={{ fontWeight: 600 }}>
                                  {cfg.minFee !== null && cfg.minFee !== undefined
                                    ? formatKES(cfg.minFee)
                                    : "None"}
                                </span>
                              </Typography>
                              <Typography variant="body2" sx={{ fontSize: "0.8rem", color: colors.grey[400] }}>
                                Max:{" "}
                                <span style={{ fontWeight: 600 }}>
                                  {cfg.maxFee !== null && cfg.maxFee !== undefined
                                    ? formatKES(cfg.maxFee)
                                    : "None"}
                                </span>
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Royalty */}
                        <TableCell>
                          {isConfigured && cfg.royaltyValue !== undefined ? (
                            <Typography variant="body2" sx={{ fontWeight: 600, color: "#22bf72" }}>
                              {cfg.royaltyType === "PERCENTAGE"
                                ? `${cfg.royaltyValue}% of sweep`
                                : `${formatKES(cfg.royaltyValue)} flat`}
                            </Typography>
                          ) : (
                            <Typography variant="body2" sx={{ color: colors.grey[400] }}>
                              —
                            </Typography>
                          )}
                        </TableCell>

                        {/* Pricing Tier */}
                        <TableCell>
                          {isCustom ? (
                            <Chip
                              label="Custom Policy"
                              size="small"
                              color="primary"
                              sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                            />
                          ) : isConfigured ? (
                            <Chip
                              label="Configured"
                              size="small"
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 700, height: 22, fontSize: "0.7rem" }}
                            />
                          ) : (
                            <Chip
                              label="Not Configured"
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                height: 22,
                                fontSize: "0.7rem",
                                color: colors.grey[400],
                                borderColor: colors.grey[600],
                              }}
                            />
                          )}
                        </TableCell>

                        {/* Status */}
                        <TableCell>
                          {cfg.isActive !== false ? (
                            <Chip
                              icon={<CheckCircleOutline fontSize="small" />}
                              label="Active"
                              color="success"
                              size="small"
                              sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
                            />
                          ) : (
                            <Chip
                              icon={<WarningAmberOutlined fontSize="small" />}
                              label="Inactive"
                              color="default"
                              size="small"
                              sx={{ height: 22, fontSize: "0.7rem" }}
                            />
                          )}
                        </TableCell>

                        {/* Action Buttons */}
                        <TableCell align="right">
                          <Stack direction="row" spacing={1} justifyContent="flex-end">
                            <Tooltip title="Test this pricing in the live fee simulator">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSimulatorCompanyId(t.id);
                                  window.scrollTo({ top: 120, behavior: "smooth" });
                                }}
                              >
                                <CalculateOutlined sx={{ fontSize: 18, color: "#1766A2" }} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Configure custom fee rates for this tenant">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleOpenEdit(t)}
                                startIcon={<EditOutlined sx={{ fontSize: 14 }} />}
                                sx={{ textTransform: "none", fontSize: "0.75rem", fontWeight: 600, py: 0.25 }}
                              >
                                Edit
                              </Button>
                            </Tooltip>

                            {isCustom && (
                              <Tooltip title="Reset to standard platform default tier">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetToStandard(t)}
                                >
                                  <RestartAltOutlined sx={{ fontSize: 18, color: colors.grey[400] }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Table Pagination */}
            <TablePagination
              component="div"
              count={filteredTenants.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}` }}
            />
          </>
        )}
        </CardContent>
      </Card>

      {/* ── Dialog / Modal: Configure Tenant Pricing ─────────────────────── */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <TuneOutlined sx={{ color: "#1766A2" }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                {editingCompany ? `Pricing Schedule: ${editingCompany.name}` : "Configure Tenant Fee Policy"}
              </Typography>
              <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                Define disbursement fee charges, floor/cap thresholds, and withdrawal royalties.
              </Typography>
            </Box>
          </Box>
          <IconButton size="small" onClick={() => setDialogOpen(false)}>
            <CloseOutlined />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ py: 2.5 }}>
          {/* Quick Preset Buttons */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400], mb: 1, display: "block" }}>
              FAST APPLY PRICING PRESET
            </Typography>
            <Grid container spacing={1.5}>
              {PRICING_PRESETS.map((preset) => (
                <Grid item xs={12} sm={6} md={3} key={preset.id}>
                  <Card
                    onClick={() => handleApplyPreset(preset)}
                    sx={{
                      cursor: "pointer",
                      p: 1.5,
                      borderRadius: 2,
                      border: `1.5px solid ${selectedPreset === preset.id ? "#1766A2" : isDark ? "rgba(255,255,255,0.08)" : "#e2e8f0"}`,
                      backgroundColor: selectedPreset === preset.id ? (isDark ? "rgba(23,102,162,0.2)" : "#eff6ff") : "transparent",
                      transition: "all 0.2s",
                      "&:hover": { borderColor: "#1766A2" },
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                      {preset.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400], fontSize: "0.68rem", display: "block", mt: 0.5 }}>
                      {preset.description}
                    </Typography>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Form Fields */}
          <Grid container spacing={2.5}>
            {/* Target Company Selector */}
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Target MFI Tenant Institution *
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={targetCompanyId ? String(targetCompanyId) : ""}
                onChange={(e) => {
                  const strVal = e.target.value;
                  setTargetCompanyId(strVal);
                  const found = tenants.find((t) => String(t.id) === strVal);
                  if (found) setEditingCompany(found);
                }}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: {
                    disablePortal: false,
                    PaperProps: {
                      sx: { maxHeight: 320, zIndex: 2500 },
                    },
                    sx: { zIndex: 2500 },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <em style={{ color: "#94a3b8" }}>— Select an MFI Tenant Institution —</em>
                </MenuItem>

                {/* Always inject the currently-editing company or targetCompanyId so the value is never out-of-range,
                    even while the full tenant list is still loading from the API */}
                {targetCompanyId && !tenants.find((t) => String(t.id) === String(targetCompanyId)) && (
                  <MenuItem key={targetCompanyId} value={String(targetCompanyId)}>
                    {editingCompany?.name || `Tenant #${String(targetCompanyId).padStart(4, "0")}`}
                  </MenuItem>
                )}

                {tenants.length === 0 ? (
                  <MenuItem disabled>
                    <em>{loading ? "Loading tenants from database…" : "No tenants found (UAT API unreachable)"}</em>
                  </MenuItem>
                ) : (
                  tenants.map((t) => (
                    <MenuItem key={t.id} value={String(t.id)}>
                      {t.name} (Tenant #{String(t.id).padStart(4, "0")})
                    </MenuItem>
                  ))
                )}
              </TextField>
            </Grid>

            {/* Policy Status Switch */}
            <Grid item xs={12} sm={6} sx={{ display: "flex", alignItems: "center" }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Policy Status: {isActive ? "Active Policy" : "Inactive / Draft"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                      Inactive policies fall back to the Standard Platform Default.
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Section 1: Disbursement Fees */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1766A2", mt: 1 }}>
                1. Loan Disbursement Fee Configuration
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Fee Assessment Method
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={feeType}
                onChange={(e) => setFeeType(e.target.value)}
              >
                <MenuItem value="PERCENTAGE">Percentage (%) of Principal Amount</MenuItem>
                <MenuItem value="FLAT">Flat Fee (Fixed KES per transaction)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Disbursement Fee Rate *
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={feeValue}
                onChange={(e) => setFeeValue(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {feeType === "PERCENTAGE" ? "%" : "KES"}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Minimum Fee Floor (KES)
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                placeholder="e.g. 20.00"
                value={minFee}
                onChange={(e) => setMinFee(e.target.value)}
                helperText="Minimum charge enforced on micro-loans"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Maximum Fee Cap (KES)
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                placeholder="e.g. 500.00"
                value={maxFee}
                onChange={(e) => setMaxFee(e.target.value)}
                helperText="Upper limit ceiling for large disbursements"
              />
            </Grid>

            {/* Section 2: Withdrawal Royalty */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#22bf72", mt: 1 }}>
                2. Treasury Withdrawal Royalty Configuration
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Royalty Assessment Method
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={royaltyType}
                onChange={(e) => setRoyaltyType(e.target.value)}
              >
                <MenuItem value="PERCENTAGE">Percentage (%) of Withdrawn Funds</MenuItem>
                <MenuItem value="FLAT">Flat Fee (Fixed KES per withdrawal)</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Withdrawal Royalty Rate *
              </Typography>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={royaltyValue}
                onChange={(e) => setRoyaltyValue(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {royaltyType === "PERCENTAGE" ? "%" : "KES"}
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Section 3: Schedule Dates */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.grey[300], mt: 1 }}>
                3. Policy Schedule (Optional)
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Effective Start Date
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={effectiveFrom}
                onChange={(e) => setEffectiveFrom(e.target.value)}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Effective End Date (Optional)
              </Typography>
              <TextField
                type="date"
                fullWidth
                size="small"
                value={effectiveTo}
                onChange={(e) => setEffectiveTo(e.target.value)}
                helperText="Leave blank for ongoing indefinite policy"
              />
            </Grid>
          </Grid>

          {/* Validation Warning Alert */}
          {validationError && (
            <Alert severity="error" sx={{ mt: 2.5 }}>
              {validationError}
            </Alert>
          )}

          {/* Live In-Modal Simulation Card */}
          <Box
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: isDark ? "rgba(0,0,0,0.25)" : "#f1f5f9",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 700, color: colors.grey[400], display: "block", mb: 1 }}>
              LIVE PREVIEW: SAMPLE KES 10,000 DISBURSEMENT UNDER THIS POLICY
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Disbursement Fee
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#1766A2" }}>
                  {formatKES(modalPreviewResult.finalFee)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Net to Borrower
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: "#22bf72" }}>
                  {formatKES(modalPreviewResult.netDisbursed)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Effective Deduction
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, color: colors.grey[100] }}>
                  {modalPreviewResult.effectiveRate.toFixed(2)}%
                </Typography>
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ textTransform: "none", color: colors.grey[300], fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={saving || Boolean(validationError)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveOutlined />}
            sx={{
              backgroundColor: "#1766A2",
              color: "#fff",
              fontWeight: 700,
              textTransform: "none",
              px: 3,
            }}
          >
            {saving ? "Saving Policy..." : "Save Pricing Policy"}
          </Button>
        </DialogActions>
      </Dialog>
        </>
      )}
    </Box>
  );
};

export default FeeConfigManagement;
